import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import axiosRetry from 'axios-retry'

import { API_URL } from '../config/api'
// Re-export types from types/index.ts
export type { Agent, Project, User } from '../types'

// API hata tipleri
export interface ApiError {
  message: string
  status: number
  code?: string
  details?: Record<string, any>
}

// Axios instance olustur - Backend API
const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 30000, // 30 saniye
  headers: {
    'Content-Type': 'application/json',
  },
})

// Retry logic - 3 kez tekrar dene
axiosRetry(api, {
  retries: 3, // 3 kez tekrar dene
  retryDelay: (retryCount) => {
    return axiosRetry.exponentialDelay(retryCount)
  },
  retryCondition: (error: AxiosError) => {
    // Sadece network hatalari ve 5xx hatalari icin retry yap
    return (
      axiosRetry.isNetworkOrIdempotentRequestError(error) ||
      (error.response?.status !== undefined && error.response.status >= 500)
    )
  },
  onRetry: (retryCount: number, error: AxiosError) => {
    console.warn(`API retry ${retryCount}/3:`, error.message)
  },
})

// Request interceptor - Authorization header ekle
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('session_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: AxiosError) => {
    console.error('Request interceptor hatasi:', error)
    return Promise.reject(error)
  }
)

// Response interceptor - Hata yonetimi
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Basarili yanit - dogrudan dondur
    return response
  },
  (error: AxiosError) => {
    const apiError = parseError(error)

    // HTTP durum koduna gore islem yap
    switch (apiError.status) {
      case 401:
        // Unauthorized - Token gecersiz veya yok
        handleUnauthorized()
        break

      case 403:
        // Forbidden - Yetkisiz erisim
        console.error('Yetkisiz erisim:', apiError.message)
        break

      case 404:
        // Not Found - Kaynak bulunamadi
        console.error('Kaynak bulunamadi:', apiError.message)
        break

      case 500:
      case 502:
      case 503:
        // Server Error - Sunucu hatasi
        console.error('Sunucu hatasi:', apiError.message)
        break

      default:
        console.error('API hatasi:', apiError.message)
    }

    return Promise.reject(apiError)
  }
)

// Hata mesajini parse et
function parseError(error: AxiosError): ApiError {
  if (error.response) {
    // Sunucudan gelen hata
    const data = error.response.data as any
    return {
      message: data?.message || data?.detail || 'Bir hata olustu',
      status: error.response.status,
      code: data?.code,
      details: data?.details,
    }
  } else if (error.request) {
    // Istek gonderildi ama yanit alinmadi
    return {
      message: 'Sunucuya ulasilamiyor. Lutfen internet baglantinizi kontrol edin.',
      status: 0,
      code: 'NETWORK_ERROR',
    }
  } else {
    // Istek olusturulurken hata
    return {
      message: error.message || 'Beklenmeyen bir hata olustu',
      status: 0,
      code: 'REQUEST_ERROR',
    }
  }
}

// 401 Unauthorized durumunda cagrilir
function handleUnauthorized() {
  // Token ve kullanici bilgilerini temizle
  localStorage.removeItem('session_token')
  localStorage.removeItem('user')

  // Login sayfasina yonlendir (sadece login sayfasinda degilsek)
  if (!window.location.pathname.includes('/login')) {
    const returnUrl = window.location.pathname + window.location.search
    window.location.href = `/login?returnUrl=${encodeURIComponent(returnUrl)}`
  }
}

// Kullanici dostu hata mesaji olustur
export function getUserFriendlyError(error: ApiError): string {
  // Ozel hata mesajlari
  const errorMessages: Record<string, string> = {
    NETWORK_ERROR: 'Internet baglantinizi kontrol edin',
    TIMEOUT: 'Istek zaman asimina ugradi',
    INVALID_CREDENTIALS: 'Kullanici adi veya sifre hatali',
    USER_NOT_FOUND: 'Kullanici bulunamadi',
    PROJECT_NOT_FOUND: 'Proje bulunamadi',
    AGENT_NOT_FOUND: 'Agent bulunamadi',
    INSUFFICIENT_BALANCE: 'Yetersiz bakiye',
    PERMISSION_DENIED: 'Bu islemi yapmaya yetkiniz yok',
  }

  if (error.code && errorMessages[error.code]) {
    return errorMessages[error.code]
  }

  // HTTP durum koduna gore mesaj
  switch (error.status) {
    case 400:
      return 'Gecersiz istek. Lutfen bilgileri kontrol edin.'
    case 401:
      return 'Oturum sureniz doldu. Lutfen tekrar giris yapin.'
    case 403:
      return 'Bu islemi yapmaya yetkiniz yok.'
    case 404:
      return 'Aradiginiz kaynak bulunamadi.'
    case 500:
      return 'Sunucu hatasi. Lutfen daha sonra tekrar deneyin.'
    case 503:
      return 'Servis su anda kullanilamiyor. Lutfen daha sonra tekrar deneyin.'
    default:
      return error.message || 'Bir hata olustu'
  }
}

export default api

// ============================================================================
// AUTH API - Kimlik dogrulama
// ============================================================================

import type { AuthResponse, LoginForm, RegisterForm, User } from '../types'

/**
 * Kullanici girisi
 * POST /api/auth/login
 */
export async function login(credentials: LoginForm): Promise<AuthResponse> {
  const { data } = await api.post<AuthResponse>('/auth/login', credentials)
  return data
}

/**
 * Kullanici kaydi
 * POST /api/auth/register
 */
export async function register(formData: RegisterForm): Promise<AuthResponse> {
  const { data } = await api.post<AuthResponse>('/auth/register', formData)
  return data
}

/**
 * Token yenileme
 * POST /api/auth/refresh
 */
export async function refreshToken(): Promise<AuthResponse> {
  const { data } = await api.post<AuthResponse>('/auth/refresh')
  return data
}

/**
 * Cikis yap
 * POST /api/auth/logout
 */
export async function logout(): Promise<void> {
  await api.post('/auth/logout')
}

/**
 * Mevcut kullanici bilgilerini getir
 * GET /api/auth/me
 */
export async function getCurrentUser(): Promise<User> {
  const { data } = await api.get<User>('/auth/me')
  return data
}

// ============================================================================
// PROJECTS API - Proje islemleri
// ============================================================================

import type { Contributor, Donor, Message, MessageCreateForm, PaginatedResponse, Project, ProjectCreateForm, ProjectFilters, ProjectUpdateForm } from '../types'

/**
 * Proje listesini getir (filtreleme ve sayfalama ile)
 * GET /api/rd/projects
 */
export async function fetchProjects(filters: ProjectFilters = {}): Promise<PaginatedResponse<Project>> {
  const { data } = await api.get<{projects: any[], count?: number, total?: number}>('/rd/projects', { params: filters })

  // Backend yaniti: {projects: [...], count: N} veya {projects: [...], total: N}
  // Frontend beklentisi: {items: [...], total: N, page: 1, page_size: 12}
  const totalCount = data.total ?? data.count ?? 0

  // Backend'den gelen veriyi frontend formatına dönüştür
  const projects = (data.projects ?? []).map((p: any) => {
    const domain = p.domain as string | undefined
    return {
      id: p.id,
      title: p.title,
      description: p.problem_statement || '',
      category: domain || 'Genel',
      emoji: getEmojiForDomain(domain),
      budget: p.bounty_amount || 0,
      agentsCount: p.participant_ids?.length || 0,
      findingsCount: 0,
      status: p.status,
      created_at: p.created_at,
      problem_statement: p.description || '',
      visibility: 'public' as const,
      current_cycle: 1,
      total_cycles_planned: 1
    }
  })

  return {
    items: projects,
    total: totalCount,
    page: filters.page ?? 1,
    per_page: 12,
    page_size: 12,
    total_pages: Math.ceil(totalCount / 12)
  }
}

// Domain'e göre emoji döndür
function getEmojiForDomain(domain: string | undefined): string {
  if (!domain) {return '🔬'}
  const emojiMap: Record<string, string> = {
    'fizik': '⚛️',
    'matematik': '📐',
    'kimya': '🧪',
    'biyoloji': '🧬',
    'mühendislik': '⚙️',
    'yazılım': '💻',
    'teknoloji': '🔬',
  }
  return emojiMap[domain.toLowerCase()] || '🔬'
}

/**
 * Tek bir projeyi getir
 * GET /api/rd/projects/:id
 */
export async function fetchProject(id: number): Promise<Project> {
  const { data } = await api.get<Project>(`/rd/projects/${id}`)
  return data
}

/**
 * Yeni proje olustur
 * POST /api/projects
 */
export async function createProject(projectData: ProjectCreateForm): Promise<Project> {
  const { data } = await api.post<Project>('/projects', projectData)
  return data
}

/**
 * Projeyi guncelle
 * PUT /api/projects/:id
 */
export async function updateProject(id: number, projectData: ProjectUpdateForm): Promise<Project> {
  const { data } = await api.put<Project>(`/projects/${id}`, projectData)
  return data
}

/**
 * Projeyi sil
 * DELETE /api/projects/:id
 */
export async function deleteProject(id: number): Promise<void> {
  await api.delete(`/projects/${id}`)
}

/**
 * Projeye katil
 * POST /api/projects/:id/join
 */
export async function joinProject(id: number): Promise<void> {
  await api.post(`/projects/${id}/join`)
}

/**
 * Projeden ayril
 * POST /api/projects/:id/leave
 */
export async function leaveProject(id: number): Promise<void> {
  await api.post(`/projects/${id}/leave`)
}

/**
 * Proje katilimcilarini getir
 * GET /api/projects/:id/contributors
 */
export async function fetchProjectContributors(id: number): Promise<Contributor[]> {
  const { data } = await api.get<Contributor[]>(`/projects/${id}/contributors`)
  return data
}

/**
 * Proje bagiscilari getir
 * GET /api/projects/:id/donors
 */
export async function fetchProjectDonors(id: number): Promise<Donor[]> {
  const { data } = await api.get<Donor[]>(`/projects/${id}/donors`)
  return data
}

/**
 * Proje mesajlarini getir
 * GET /api/projects/:id/messages
 */
export async function fetchProjectMessages(id: number): Promise<Message[]> {
  const { data } = await api.get<Message[]>(`/projects/${id}/messages`)
  return data
}

/**
 * Projeye mesaj gonder
 * POST /api/projects/:id/messages
 */
export async function sendProjectMessage(id: number, messageData: MessageCreateForm): Promise<Message> {
  const { data } = await api.post<Message>(`/projects/${id}/messages`, messageData)
  return data
}

/**
 * Kullanicinin projelerini getir
 * GET /api/users/:userId/projects
 */
export async function fetchUserProjects(userId: number): Promise<Project[]> {
  const { data } = await api.get<Project[]>(`/users/${userId}/projects`)
  return data
}

// ============================================================================
// AGENTS API - Agent islemleri
// ============================================================================

import type { Agent, AgentCreateForm, AgentFilters, AgentUpdateForm } from '../types'

/**
 * Agent listesini getir (filtreleme ve sayfalama ile)
 * GET /api/discover/agents
 */
export async function fetchAgents(filters: AgentFilters = {}): Promise<PaginatedResponse<Agent>> {
  const { data } = await api.get<any>('/discover/agents', { params: filters })

  // Backend yaniti: {agents: [...], total: N} veya {items: [...], total: N}
  // Frontend beklentisi: {items: [...], total: N, page: 1, page_size: 12}
  const agents = data.agents || data.items || []
  const total = data.total || agents.length

  return {
    items: agents,
    total: total,
    page: filters.page ?? 1,
    per_page: 12,
    page_size: 12,
    total_pages: Math.ceil(total / 12)
  }
}

/**
 * Tek bir agenti getir
 * GET /api/agents/:id
 */
export async function fetchAgent(id: number): Promise<Agent> {
  const { data } = await api.get<Agent>(`/agents/${id}`)
  return data
}

/**
 * Yeni agent olustur
 * POST /api/agents
 */
export async function createAgent(agentData: AgentCreateForm): Promise<Agent> {
  const { data } = await api.post<Agent>('/agents', agentData)
  return data
}

/**
 * Agenti guncelle
 * PUT /api/agents/:id
 */
export async function updateAgent(id: number, agentData: AgentUpdateForm): Promise<Agent> {
  const { data } = await api.put<Agent>(`/agents/${id}`, agentData)
  return data
}

/**
 * Agenti sil
 * DELETE /api/agents/:id
 */
export async function deleteAgent(id: number): Promise<void> {
  await api.delete(`/agents/${id}`)
}

/**
 * Kullanicinin agentlarini getir
 * GET /api/users/:userId/agents
 */
export async function fetchUserAgents(userId: number): Promise<Agent[]> {
  const { data } = await api.get<Agent[]>(`/users/${userId}/agents`)
  return data
}

// ============================================================================
// USERS API - Kullanici islemleri
// ============================================================================

import type { ProfileUpdateForm, UserStats } from '../types'

/**
 * Kullanici profilini getir
 * GET /api/users/:username (ID veya display_name)
 */
export async function fetchUserProfile(usernameOrId: string): Promise<User> {
  const { data } = await api.get<User>(`/profile/user/${usernameOrId}`)
  return data
}

/**
 * Kullanici istatistiklerini getir
 * GET /api/users/:userId/stats
 */
export async function fetchUserStats(userId: number): Promise<UserStats> {
  const { data } = await api.get<UserStats>(`/users/${userId}/stats`)
  return data
}

/**
 * Profili guncelle
 * PUT /api/users/:userId
 */
export async function updateProfile(userId: number, profileData: ProfileUpdateForm): Promise<User> {
  const { data } = await api.put<User>(`/users/${userId}`, profileData)
  return data
}

/**
 * Liderlik tablosunu getir
 * GET /api/leaderboard
 */
export async function fetchLeaderboard(type: 'users' | 'projects' | 'agents' = 'users'): Promise<any[]> {
  const { data } = await api.get<any[]>('/leaderboard', { params: { type } })
  return data
}

/**
 * Kesfet sayfasi icin onerileri getir
 * GET /api/discover/projects + /api/discover/agents
 */
export async function fetchDiscoverContent(): Promise<{ featured_projects: Project[], featured_agents: Agent[] }> {
  const [projectsRes, agentsRes] = await Promise.all([
    api.get('/discover/projects', { params: { page: 1, page_size: 6 } }),
    api.get('/discover/agents', { params: { page: 1, page_size: 6 } }),
  ])
  const projectsData = projectsRes.data as { items?: Project[] }
  const agentsData = agentsRes.data as { items?: Agent[] }
  return {
    featured_projects: projectsData.items ?? [],
    featured_agents: agentsData.items ?? [],
  }
}

// ============================================================================
// EXTENDED API - Mockup to React Migration
// ============================================================================

import type { AdminMetrics, CloudPlatform, Finding, PlatformStats, Question, Timeline } from '../types/api'

/**
 * Proje bulgularini getir
 * GET /api/research-projects/:id/findings
 */
export async function fetchProjectFindings(projectId: number): Promise<Finding[]> {
  const { data } = await api.get<Finding[]>(`/research-projects/${projectId}/findings`)
  return data
}

/**
 * Proje sorularini getir
 * GET /api/research-projects/:id/questions
 */
export async function fetchProjectQuestions(projectId: number): Promise<Question[]> {
  const { data } = await api.get<Question[]>(`/research-projects/${projectId}/questions`)
  return data
}

/**
 * Proje zaman cizelgesini getir
 * GET /api/timeline/:projectId
 */
export async function fetchTimeline(projectId: number): Promise<Timeline[]> {
  const { data } = await api.get<Timeline[]>(`/timeline/${projectId}`)
  return data
}

/**
 * Kullanici zaman cizelgesini getir
 * GET /api/timeline/user/:username
 */
export async function fetchUserTimeline(username: string): Promise<Timeline[]> {
  const { data } = await api.get<Timeline[]>(`/timeline/user/${username}`)
  return data
}

/**
 * Proje agentlarini getir
 * GET /api/project-agents/:projectId
 */
export async function fetchProjectAgents(projectId: number): Promise<Agent[]> {
  const { data } = await api.get<Agent[]>(`/project-agents/${projectId}`)
  return data
}

/**
 * Proje bagislarini getir
 * GET /api/donations/project/:projectId
 */
export async function fetchProjectDonations(projectId: number): Promise<Donor[]> {
  const { data } = await api.get<Donor[]>(`/donations/project/${projectId}`)
  return data
}

/**
 * Admin metriklerini getir
 * GET /api/admin/metrics
 */
export async function fetchAdminMetrics(): Promise<AdminMetrics> {
  const { data } = await api.get<AdminMetrics>('/admin/metrics')
  return data
}

/**
 * Admin kullanici listesini getir
 * GET /api/admin/users
 */
export async function fetchAdminUsers(): Promise<User[]> {
  const { data } = await api.get<User[]>('/admin/users')
  return data
}

/**
 * Admin son aktiviteleri getir
 * GET /api/admin/activity
 */
export async function fetchAdminActivity(): Promise<any[]> {
  const { data } = await api.get<any[]>('/admin/activity')
  return data
}

/**
 * Kullaniciyi askiya al
 * POST /api/admin/users/:id/suspend
 */
export async function suspendUser(userId: string): Promise<void> {
  await api.post(`/admin/users/${userId}/suspend`)
}

/**
 * Bulut platformlarini getir
 * GET /api/cloud/platforms
 */
export async function fetchCloudPlatforms(): Promise<CloudPlatform[]> {
  const { data } = await api.get<CloudPlatform[]>('/cloud/platforms')
  return data
}

/**
 * Platform durumunu getir
 * GET /api/cloud/:platform/status
 */
export async function fetchCloudPlatformStatus(platform: string): Promise<CloudPlatform> {
  const { data } = await api.get<CloudPlatform>(`/cloud/${platform}/status`)
  return data
}

/**
 * Platform API anahtari ekle
 * POST /api/cloud/:platform/keys
 */
export async function addCloudPlatformKey(platform: string, apiKey: string): Promise<void> {
  await api.post(`/cloud/${platform}/keys`, { api_key: apiKey })
}

/**
 * Platform istatistiklerini getir
 * GET /api/platform/stats
 */
export async function fetchPlatformStats(): Promise<PlatformStats> {
  const { data } = await api.get<PlatformStats>('/platform/stats')
  return data
}

/**
 * Onerilen projeleri getir
 * GET /api/research-projects?featured=true
 */
export async function fetchFeaturedProjects(): Promise<Project[]> {
  const { data } = await api.get<{projects: Project[]}>('/research-projects', {
    params: { featured: true }
  })
  return data.projects ?? []
}

/**
 * En iyi agentlari getir
 * GET /api/user-agents?top=true
 */
export async function fetchTopAgents(): Promise<Agent[]> {
  const { data } = await api.get<PaginatedResponse<Agent>>('/user-agents', {
    params: { top: true }
  })
  return data.items ?? []
}

/**
 * Kullanici kazanclarini getir
 * GET /api/earnings/user/:username
 */
export async function fetchUserEarnings(username: string): Promise<any> {
  const { data } = await api.get(`/earnings/user/${username}`)
  return data
}

/**
 * Proje ID'sine gore getir (alias for fetchProject)
 * GET /api/research-projects/:id
 */
export async function fetchProjectById(id: number): Promise<Project> {
  return fetchProject(id)
}

/**
 * Agent ID'sine gore getir (alias for fetchAgent)
 * GET /api/user-agents/:id
 */
export async function fetchAgentById(id: string): Promise<Agent> {
  const { data } = await api.get<Agent>(`/user-agents/${id}`)
  return data
}
